import { useState, useCallback, ComponentProps } from 'react';
import styled from 'styled-components';
import { Button, Box, Divider } from '@mui/material';
import TeamTagsAutocomplete from 'components/TeamTagsAutocomplete';
import DatePicker from 'components/DatePicker';
import { Alert, AlertColor as Color } from '@mui/material';
import { Formik } from 'formik';
import { LinearBuffer } from 'components/Progress';
import { Close as CloseIcon } from '@mui/icons-material';
import useDeactivateTeams from 'hooks/useDeactivateTeams';
import useReactivateTeamsConfirmModal from 'hooks/useReactivateTeamsConfirmModal';
import useAppNavigation from 'hooks/useAppNavigation';
import { PromiseAllSettledStatus } from 'services/constants';
import { REMOVE_TAG_TEXT } from '@constants';
import cn from 'classnames';
import { updateTeam } from 'thunk';
import Modal from 'shared/Modal';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { useCsmAssignments } from 'CsmModule/hooks/useCsmAssignments';
import { formatDate } from 'utils';
import { CsmSelector } from 'CsmModule/components/CsmSelector';
import useTeams from 'hooks/useTeams';
import { useMount } from 'react-use';
import { useCsmSelector } from 'CsmModule/hooks/useCsmSelector';
import { useNotification } from 'hooks/useNotification';

const paramsToRemove = ['tag'];

const removeUnusedParams = (record: Record<string, any>) => {
  paramsToRemove.forEach((param: string) => delete record[param]);
};

type UpdateTeamFormProps = {
  teamId: number;
  closeModal: () => void;
  isOpen: boolean;
};

const FormHeader = styled.h2`
  word-break: break-all;
  max-width: 100%;
`;

type FormStylerProps = {
  isLoading: boolean;
};

const FormStyler = styled.div<FormStylerProps>`
  .name_field,
  .tag_field,
  .skip_cc_field,
  .days_until_trial_expiration_field,
  .remove_date_field {
    display: inline-flex;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .tag_field,
  .remove_date_field {
    margin-left: 2px;
  }

  .info-icon {
    position: relative;
    top: 7px;
  }

  .days_until_trial_expiration_field {
    align-items: flex-start;
    margin-left: 1px;
  }
  .submit-button {
    margin-top: 5px;
  }

  .deactivate-team-button {
    margin-left: 10px;
    margin-top: 5px;
    background-color: red;
    color: white;
  }

  .reactivate-team-button {
    margin-left: 10px;
    margin-top: 5px;
    background-color: #376fd0;
    color: white;
  }

  .close-icon {
    float: right;
    cursor: ${(props: any) => (props.isLoading ? 'not-allowed' : 'pointer')};
  }
`;

const RemoveDateText = '-remove-date-';

const UpdateTeamForm = ({ teamId, closeModal, isOpen }: UpdateTeamFormProps) => {
  const { fetchTeams, teamHash, isFetchingTeams } = useTeams();
  const selectedTeam = teamHash[teamId];
  const { showSuccess, showError } = useNotification();
  useMount(() => {
    fetchTeams({ teamIds: [teamId] });
  });

  const stateId = `team-${teamId}`;
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const { updateCsmAssignment, createCsmAssignment, csmAssignmentByTeam } = useCsmAssignments({
    isOff: true,
  });
  const { error: updateTeamError, isRequesting } = selectLocalData({ stateId });
  const [updateTeamClicked, setUpdateTeamClicked] = useState(false);
  const errorMessage = updateTeamError?.data?.error;
  const status = updateTeamError?.data?.status;
  const csmAssignment = csmAssignmentByTeam[teamId];
  const teamCsmAssignmentAccountId = csmAssignment?.account_id;
  // const teamCsmAssignmentName = csmAssignment?.account_name;
  const teamCsmAssignmentStartDate = formatDate(csmAssignment?.start_date);
  const teamCsmAssignmentEndDate = formatDate(csmAssignment?.end_date);
  const hasCsmAssignment = !!csmAssignment;
  const { onCsmSelectorClick, selectedCsm } = useCsmSelector({
    accountId: teamCsmAssignmentAccountId || null,
  });

  const alertProps: {
    message: string;
    severity: Color;
  } = {
    message:
      status >= 300
        ? errorMessage || 'Update team failed. Please try again.'
        : 'Update team successful!',
    severity: errorMessage ? 'error' : 'success',
  };
  const isLoading = isRequesting || isFetchingTeams;
  const [tags, setTags] = useState(new Set(selectedTeam?.tags || []));
  const [removeDate, setRemoveDate] = useState('');

  const onUpdateFormAlertClose = () => {
    setUpdateTeamClicked(false);
  };

  const { DeactivateTeamsConfirmModal, handleOpenDeactivateTeamsModal } = useDeactivateTeams();
  const { ReactivateTeamsConfirmModal, handleOpenReactivateTeamsModal } =
    useReactivateTeamsConfirmModal();

  const isDeactivated = Boolean(selectedTeam?.deactivated_at && selectedTeam?.deactivated_by);

  const { navigateToTeamsTable, navigateToTeamsV2Table } = useAppNavigation();

  const onTagsChange = (next: string[]) => {
    const prevHadRemove = tags.has(REMOVE_TAG_TEXT);
    const nextHasRemove = next.includes(REMOVE_TAG_TEXT);
    const cleaned =
      prevHadRemove && !nextHasRemove ? next.filter((str) => !str.includes(RemoveDateText)) : next;
    setTags(new Set(cleaned));
  };

  const preventSubmissionOnEnter = (keyEvent: any) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  const onRemoveDateChange =
    (setFieldValue: any) => (event: React.ChangeEvent<{ value: string }>) => {
      const selectedDay = event.target.value;
      const label = RemoveDateText + selectedDay;
      setRemoveDate(selectedDay);
      setFieldValue('removeDate', '');
    };

  const onDeactivateClick = () => {
    if (selectedTeam) {
      handleOpenDeactivateTeamsModal({
        teamIds: [teamId],
        skipStateUpdate: true,
        onSuccess: ({ response }) => {
          const result = response?.[0] || {};

          if (result.status === PromiseAllSettledStatus.Fulfilled) {
            // ✅ FIX: Replace alert() with toast notification
            showSuccess('Deactivate Team Successful. Navigating to Teams 2.0');
            navigateToTeamsV2Table();
          } else {
            const errorMsg = result?.reason?.data?.errors?.[0] || '';
            // ✅ FIX: Replace alert() with toast notification
            showError(`Deactivate Team Failed. Please try again. \n\nError: ${errorMsg}`);
          }
        },
      });
    }
  };

  const onReactivateClick = () => {
    handleOpenReactivateTeamsModal({
      teamIds: [teamId],
      onSuccess: ({ response }) => {
        const result = response?.[0] || {};

        if (result.status === PromiseAllSettledStatus.Fulfilled) {
          // ✅ FIX: Replace alert() with toast notification
          showSuccess('Reactivate Team Successful');
          window.location.reload();
        } else {
          const errorMsg = result?.reason?.data?.errors?.[0] || '';
          // ✅ FIX: Replace alert() with toast notification
          showError(`Reactivate Team Failed. Please try again. \n\nError: ${errorMsg}`);
        }
      },
    });
  };

  const onFormSubmit = useCallback(
    (additionalProps: Record<string, any>) =>
      (values: Record<string, any>, { setSubmitting }: any) => {
        if (!selectedTeam) return;
        const { tags } = additionalProps;
        const { days_until_trial_expiration, id } = selectedTeam;
        const valuesToSubmit = { ...values };

        /** If trial expire is the same, don't send it back to BE for update */
        if (days_until_trial_expiration === valuesToSubmit.days_until_trial_expiration) {
          delete valuesToSubmit.days_until_trial_expiration;
        }

        removeUnusedParams(valuesToSubmit);

        const tagsList = Array.from(tags);

        const tagParams = tagsList?.length ? { tags: tagsList } : { clear_tags: true };

        const payload = {
          ...valuesToSubmit,
          ...tagParams,
          team_id: id,
        };
        dispatchLocal(updateTeam, {
          params: payload,
          stateId,
          onSuccessCallback: ({ response }) => {
            fetchTeams({ teamIds: [id] });
          },
        });

        /**
         * Update or Create CSM assignment for a Team
         */
        if (selectedCsm) {
          const csmAssignmentPayload = {
            mosaicAccountId: selectedCsm.accountId,
            mosaicTeamId: teamId,
            csmName: selectedCsm.name,
            /**
             * Note that if startDate = null, BE will set it to 01-01-1970
             */
            startDate: valuesToSubmit.teamCsmAssignmentStartDate || selectedTeam?.created_at,
            endDate: valuesToSubmit.teamCsmAssignmentEndDate || null,
          };

          if (csmAssignment) {
            updateCsmAssignment({
              id: csmAssignment.id,
              ...csmAssignmentPayload,
            });
          } else {
            createCsmAssignment(csmAssignmentPayload);
          }
        }
        setUpdateTeamClicked(true);
        setSubmitting(false);
      },
    [
      selectedTeam,
      dispatchLocal,
      stateId,
      selectedCsm,
      fetchTeams,
      teamId,
      csmAssignment,
      updateCsmAssignment,
      createCsmAssignment,
    ],
  );

  const handleCsmSelectorClick: ComponentProps<typeof CsmSelector>['onClick'] = (e) => {
    onCsmSelectorClick({ target: e.currentTarget });
  };

  const handleCloseModal = () => {
    if (isLoading) return undefined;

    closeModal();
  };

  return (
    <Modal onClose={handleCloseModal} open={isOpen} modalWidth={600}>
      <FormStyler isLoading={isLoading}>
        <FormHeader>
          Editing team: {selectedTeam?.name}{' '}
          <CloseIcon onClick={handleCloseModal} className="close-icon" />
        </FormHeader>
        {updateTeamClicked && !isLoading && (
          <Alert onClose={onUpdateFormAlertClose} severity={alertProps.severity}>
            {alertProps.message}
          </Alert>
        )}
        <Divider />
        {isLoading ? (
          <LinearBuffer />
        ) : (
          <Formik
            initialValues={{
              // name: selectedTeam?.name || "",
              days_until_trial_expiration: selectedTeam?.days_until_trial_expiration ?? 0,
              tag: '',
              removeDate: '',
              // teamCsmAssignmentAccountId,
              // teamCsmAssignmentName,
              teamCsmAssignmentStartDate,
              teamCsmAssignmentEndDate,
            }}
            enableReinitialize
            onSubmit={onFormSubmit({ tags })}
          >
            {({ handleSubmit, setFieldValue, touched, values, errors, isSubmitting }) => (
              <form onSubmit={handleSubmit} onKeyDown={preventSubmissionOnEnter}>
                {/* {errors.name && touched.name && (
            <Alert severity="error">{errors.name}</Alert>
          )} */}
                {/* <FormControlLabel
            className="name_field"
            control={<TextField label="Team Name" />}
            name="name"
            label=""
            value={values.name}
            onChange={handleChange}
          /> */}
                <Box
                  sx={{
                    display: 'flex',
                    paddingTop: 10,
                    alignItems: 'baseline',
                    columnGap: '50px',
                  }}
                >
                  {/* <Box
                    display={"flex"}
                    flexDirection="column"
                    width="fit-content"
                    gap={"7px"}
                  >
                    <NumberFormat
                      allowNegative={false}
                      allowLeadingZeros={false}
                      allowEmptyFormatting={false}
                      decimalScale={0}
                      isAllowed={({ floatValue }) =>
                        floatValue === undefined || floatValue >= 1
                      }
                      value={values.teamCsmAssignmentAccountId}
                      customInput={TextField}
                      name="teamCsmAssignmentAccountId"
                      onChange={handleChange}
                    />
                    <InputLabel style={{ fontSize: "0.7rem" }}>
                      CSM's Account Id
                    </InputLabel>
                  </Box> */}
                  {/* <FormControlLabel
                    control={<TextField helperText="CSM's name" />}
                    label
                    name="teamCsmAssignmentName"
                    value={values.teamCsmAssignmentName}
                    onChange={handleChange}
                  /> */}
                  <CsmSelector
                    accountId={selectedCsm?.accountId || null}
                    onClick={handleCsmSelectorClick}
                    tooltip="Select Customer Success Manager for this team"
                  />

                  <DatePicker
                    label="CSM Start Date"
                    helperText="CSM's start date"
                    value={values.teamCsmAssignmentStartDate}
                    onChange={(newDate) =>
                      setFieldValue('teamCsmAssignmentStartDate', newDate ?? '')
                    }
                  />
                  <DatePicker
                    label="CSM End Date"
                    helperText="CSM's end date"
                    value={values.teamCsmAssignmentEndDate}
                    onChange={(newDate) => setFieldValue('teamCsmAssignmentEndDate', newDate ?? '')}
                  />
                </Box>

                {/* ----------------------------------- for remove date ---------------------------------- */}
                {/* {hasRemoveTag ? (
              <FormControlLabel
                className="remove_date_field"
                control={
                  <TextField
                    onChange={onRemoveDateChange(setFieldValue)}
                    helperText="Remove date"
                    type="date"
                    value={removeDate}
                  />
                }
                label
                name="removeDate"
                value={values.removeDate}
                onChange={handleChange}
              />
            ) : null} */}
                <Box
                  sx={{
                    mb: 2,
                  }}
                >
                  <TeamTagsAutocomplete
                    value={Array.from(tags)}
                    onChange={onTagsChange}
                    label="Tags"
                    getChipColor={(tag) => (tag === REMOVE_TAG_TEXT ? 'error' : 'primary')}
                  />
                </Box>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                  className="submit-button"
                >
                  {isSubmitting ? 'Please wait' : 'Update Team'}
                </Button>
                <Button
                  disabled={isSubmitting}
                  className={cn({
                    'reactivate-team-button': isDeactivated,
                    'deactivate-team-button': !isDeactivated,
                  })}
                  onClick={
                    selectedTeam?.deactivated_by && selectedTeam?.deactivated_at
                      ? onReactivateClick
                      : onDeactivateClick
                  }
                >
                  {isDeactivated ? 'Reactivate Team' : 'Deactivate Team'}
                </Button>
              </form>
            )}
          </Formik>
        )}
        {DeactivateTeamsConfirmModal}
        {ReactivateTeamsConfirmModal}
      </FormStyler>
    </Modal>
  );
};

export default UpdateTeamForm;
